import { Dispatch, useCallback, useContext, useReducer } from "react";
import CalculationResult from "./CalculationResult";
import { IQueryParameters, RateCalculation } from "./Models";
import NowaForm from "./NowaForm";
import Intro from "./Intro";
import { debounce, getPathWithSlash } from "./utils/functional";
import HolidaysContext from "./HolidaysContext";
import { getLocale } from "./i18n";
import { LoadIndicator } from "devextreme-react/load-indicator";

interface IBadRequestResponse {
    status: number;
    title: string;
    traceId: string;
    type: string;
    errors: { [key in string]?: Array<string> }
}

const getDateFormat = (date: Date) => {

    var mm = date.getMonth() + 1; // getMonth() is zero-based
    var dd = date.getDate();

    return [date.getFullYear(),
    (mm > 9 ? '' : '0') + mm,
    (dd > 9 ? '' : '0') + dd
    ].join('-');
}

const path = getPathWithSlash();

const generateQuery = ({ fromDate, toDate, lag, spread, notional, rounding, dayCountConvention, bankingDayConvention, rateConvention, rateFloor, strike }: IQueryParameters) => {
    const url = path + "api/calculate";
    const query = `from=${getDateFormat(fromDate)}&to=${getDateFormat(toDate)}&lag=${lag}`
        + `&spread=${spread}&notional=${notional}&daycountconvention=${dayCountConvention}`
        + `&rateconvention=${rateConvention}&rounding=${rounding}`
        + `&bankingDayConvention=${bankingDayConvention}`
        + `&floor=${rateFloor}&strike=${strike}`

    return `${url}?${query}`;
}

let incrementer = 0;
const query = (params: IQueryParameters, dispatch: Dispatch<Action>) => {
    const url = generateQuery(params);

    let next = ++incrementer;

    const headers = {
        'Accept-Language': getLocale()
    };

    fetch(url, { headers })
        .then(response => {
            if (next !== incrementer) return; // we only want to keep the last call
            
            if (response.status !== 200 && response.status !== 400) throw new Error(`Network error (status code ${response.status})`);

            response.json().then(json => {
                if (response.ok) {
                    dispatch({ type: "success", data: json })
                    return;
                }

                if (response.status === 400) {
                    const badRequestResponse = json as IBadRequestResponse;

                    const errors = Object.keys(badRequestResponse.errors)
                        .map(key => badRequestResponse.errors[key] as string[])
                        .reduce((next, acc) => [...acc, ...next]);

                    dispatch({ type: "error", errors });
                    return;
                }

                dispatch({ type: "error", errors: ["An unexpected error has occurred."] });
            })
        }).catch(error => {
            if (next !== incrementer) return; // we only want to keep the last call
            console.error(error);
            dispatch({ type: "error", errors: ["An unexpected error has occurred with the network call."] });
        });;
}

const debouncedQuery = debounce(query, 250);

type State = { loading: boolean; errors: Array<string>; calculation: RateCalculation | null };
type Action =
    | { type: "success", data: RateCalculation }
    | { type: "error", errors: string[] }
    | { type: "fetch" }


const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case "success":
            return {
                ...state,
                loading: false,
                calculation: action.data
            }
        case "error":
            return {
                ...state,
                loading: false,
                errors: action.errors
            };
        case "fetch":
            return {
                ...state,
                loading: true,
                errors: [],
                calculation: null
            }
    }
};

const NowaApp = () => {

    const [state, dispatch] = useReducer(reducer, {
        loading: false,
        errors: [],
        calculation: null
    })

    const { loading, calculation, errors } = state;
    const { holidays } = useContext(HolidaysContext);

    const onFormSubmitted = useCallback((input: IQueryParameters) => {
        debouncedQuery(input, dispatch);
        dispatch({ type: "fetch" });
    }, [dispatch]);

    const content = loading
        ? <div style={{ display: "flex", justifyContent: "center", padding: "1rem" }}><LoadIndicator className="calculation-result-load-indicator" /></div>
        : <>
            {calculation !== null ? <CalculationResult calculation={calculation} /> : null}
            {errors.map(error => <p key={error} className="error"><i className="dx-icon dx-icon-warning" /> {error}</p>)}
        </>

    return <>
        <div className="card left-edge" style={{ marginBottom: "2rem" }} >
            <Intro />
        </div>

        <div className="card">
            <NowaForm onSubmit={onFormSubmitted} holidays={holidays} />
            {content}
        </div>
    </>
}

export default NowaApp